import { useState } from 'react';
import { useToast } from '../../contexts/ToastContext';
import { isAccountModelUsable, useMoveAccountModelAgents, useUpdateAccountModel } from '../../hooks/useAccountModels';
import type {
  AccountAllowedModel,
  AccountAllowedModelGroup,
  AccountModelInUseAgent,
  AccountModelProvider,
} from '../../types/accountModels';
import { asApiRefusal, refusalReason } from '../../utils/apiRefusal';
import { PolicySwitch } from './PolicySwitch';
import { DisallowInUseDialog, accountModelName, type DisallowInUseState } from './DisallowInUseDialog';

/**
 * Settings › Account › Models — Allowed models (task 1147, steps 6–7).
 *
 * One table per provider the account has on. Each row is a catalog Model with
 * its context window, price, how many of the account's agents use it, an
 * Allowed switch and a Default radio. A Model the account's own key cannot
 * reach is marked "Not on your key" and can be neither allowed nor default.
 *
 * Turning a Model off always asks the service: when agents still use it the
 * service refuses with 409 `{ usedByAgents }`, and the disallow-in-use dialog
 * offers to move them to the provider default first. The provider default
 * cannot be turned off directly; another default has to be chosen first, since
 * the default is where moved agents go.
 */

const formatPrice = (value: string | number) => {
  const n = typeof value === 'number' ? value : Number.parseFloat(value);
  return Number.isFinite(n) ? n.toFixed(2) : '—';
};

/** The agents a 409 names, wherever the service's error envelope put them. */
function usedByAgentsFrom(error: unknown): AccountModelInUseAgent[] | null {
  const refusal = asApiRefusal(error) as { status?: number; response?: unknown; details?: unknown };
  if (refusal.status !== 409) return null;
  const candidates = [
    (refusal.response as { usedByAgents?: unknown } | undefined)?.usedByAgents,
    (refusal.response as { details?: { usedByAgents?: unknown } } | undefined)?.details?.usedByAgents,
    (refusal.details as { usedByAgents?: unknown } | undefined)?.usedByAgents,
  ];
  const found = candidates.find(Array.isArray);
  return found ? (found as AccountModelInUseAgent[]) : [];
}

interface AllowedModelsSectionProps {
  groups: AccountAllowedModelGroup[];
  providers: AccountModelProvider[];
  canManage: boolean;
}

export function AllowedModelsSection({ groups, providers, canManage }: AllowedModelsSectionProps) {
  const toast = useToast();
  const update = useUpdateAccountModel();
  const move = useMoveAccountModelAgents();
  const [inUse, setInUse] = useState<DisallowInUseState | null>(null);
  /**
   * The Model just chosen as default, held until the re-read lands. The radio
   * is controlled by `isDefault`, so without this it snaps back to unchecked
   * for the length of the PATCH and the refetch (seen in live verification).
   */
  const [pendingDefaultId, setPendingDefaultId] = useState<string | null>(null);

  const providerOn = (group: AccountAllowedModelGroup) => {
    const provider = providers.find((p) => p.id === group.provider.id);
    return (provider ? provider.enabled : true) && group.provider.enabled !== false;
  };
  const visibleGroups = groups.filter(providerOn);

  const busyModelId = update.isPending ? update.variables?.modelId : undefined;

  const handleAllowed = async (group: AccountAllowedModelGroup, model: AccountAllowedModel) => {
    if (!model.allowed) {
      try {
        await update.mutateAsync({ modelId: model.id, data: { allowed: true } });
      } catch (error) {
        toast.error(`Could not allow ${accountModelName(model)}`, refusalReason(error));
      }
      return;
    }
    try {
      await update.mutateAsync({ modelId: model.id, data: { allowed: false } });
    } catch (error) {
      const agents = usedByAgentsFrom(error);
      if (agents) {
        const target = group.models.find((m) => m.isDefault && m.id !== model.id && isAccountModelUsable(group, m)) ?? null;
        setInUse({ model, providerName: group.provider.displayName, agents, target });
        return;
      }
      toast.error(`Could not turn off ${accountModelName(model)}`, refusalReason(error));
    }
  };

  const handleDefault = async (model: AccountAllowedModel) => {
    if (model.isDefault) return;
    setPendingDefaultId(model.id);
    try {
      await update.mutateAsync({ modelId: model.id, data: { isDefault: true } });
    } catch (error) {
      toast.error(`Could not make ${accountModelName(model)} the default`, refusalReason(error));
    } finally {
      setPendingDefaultId(null);
    }
  };

  const confirmMove = async () => {
    if (!inUse?.target) return;
    const { model, target } = inUse;
    try {
      const { movedCount, failed = [] } = await move.mutateAsync({ modelId: model.id, targetModelId: target.id });
      if (failed.length > 0) {
        // Some agents stayed on the Model, so turning it off would be refused
        // again. Say which ones and leave the Model on.
        toast.error(
          `Moved ${movedCount} of ${movedCount + failed.length} agents; ${accountModelName(model)} stays on`,
          failed.map((agent) => `${agent.name}: ${agent.error}`).join('; '),
        );
        setInUse(null);
        return;
      }
      await update.mutateAsync({ modelId: model.id, data: { allowed: false } });
      toast.success(
        `Moved ${movedCount} ${movedCount === 1 ? 'agent' : 'agents'} to ${accountModelName(target)}`,
        `${accountModelName(model)} is now turned off for this account.`,
      );
      setInUse(null);
    } catch (error) {
      toast.error(`Could not turn off ${accountModelName(model)}`, refusalReason(error));
      setInUse(null);
    }
  };

  return (
    <section
      data-testid="account-models-allowed"
      className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800"
    >
      <h2 className="text-base font-semibold text-gray-900 dark:text-white">Allowed models</h2>
      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
        Models from the platform catalog that agents in this account may use. New Models added to the catalog are
        allowed within an allowed provider unless you turn them off.
      </p>

      {visibleGroups.length === 0 && (
        <p className="mt-4 text-sm text-gray-500 dark:text-gray-400" data-testid="account-models-no-allowed">
          No provider is on for this account, so no Model can be used. Turn a provider on above.
        </p>
      )}

      {visibleGroups.map((group) => {
        const allowedCount = group.models.filter((m) => isAccountModelUsable(group, m)).length;
        return (
          <div key={group.provider.id} className="mt-5" data-testid={`allowed-models-${group.provider.key}`}>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
              {group.provider.displayName}{' '}
              <span className="font-normal text-gray-500 dark:text-gray-400">
                · {allowedCount} of {group.models.length} allowed
              </span>
            </h3>
            <div className="mt-2 overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
              <table className="min-w-full divide-y divide-gray-200 text-sm dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-900/40">
                  <tr className="text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    <th scope="col" className="px-3 py-2">Model</th>
                    <th scope="col" className="px-3 py-2 text-right">Context</th>
                    <th scope="col" className="px-3 py-2 text-right">$/MTok in · out</th>
                    <th scope="col" className="px-3 py-2">Used by</th>
                    <th scope="col" className="px-3 py-2 text-center">Allowed</th>
                    <th scope="col" className="px-3 py-2 text-center">Default</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {group.models.map((model) => {
                    const notOnKey = model.reachable === false;
                    const usable = isAccountModelUsable(group, model);
                    const name = accountModelName(model);
                    const busy = busyModelId === model.id;
                    const allowedLocked = !canManage || notOnKey || busy || (model.allowed && model.isDefault);
                    const defaultLocked = !canManage || notOnKey || busy || !usable;
                    return (
                      <tr
                        key={model.id}
                        data-testid={`allowed-model-row-${model.id}`}
                        data-usable={usable ? 'true' : 'false'}
                        className={usable ? undefined : 'opacity-60'}
                      >
                        <td className="px-3 py-2">
                          <span className="font-mono font-semibold text-gray-900 dark:text-white">{name}</span>
                          {notOnKey && (
                            <span
                              data-testid={`not-on-your-key-${model.id}`}
                              className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800 dark:bg-amber-900/40 dark:text-amber-300"
                            >
                              Not on your key
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-gray-700 dark:text-gray-300">
                          {model.contextWindow.toLocaleString('en-US')}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-gray-700 dark:text-gray-300">
                          {formatPrice(model.inputPriceMTok)} · {formatPrice(model.outputPriceMTok)}
                        </td>
                        <td className="px-3 py-2 text-gray-700 dark:text-gray-300" data-testid={`used-by-${model.id}`}>
                          {model.usedByCount > 0 ? (
                            <>
                              <b>{model.usedByCount}</b> {model.usedByCount === 1 ? 'agent' : 'agents'}
                            </>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td className="px-3 py-2 text-center">
                          <PolicySwitch
                            checked={model.allowed && !notOnKey}
                            onToggle={() => handleAllowed(group, model)}
                            disabled={allowedLocked}
                            label={`Allow ${name}`}
                            title={
                              model.allowed && model.isDefault
                                ? `Choose another ${group.provider.displayName} default before turning this Model off`
                                : undefined
                            }
                            testId={`allowed-toggle-${model.id}`}
                          />
                        </td>
                        <td className="px-3 py-2 text-center">
                          <input
                            type="radio"
                            name={`default-model-${group.provider.id}`}
                            aria-label={`Make ${name} the ${group.provider.displayName} default`}
                            checked={pendingDefaultId ? pendingDefaultId === model.id : model.isDefault}
                            disabled={defaultLocked}
                            onChange={() => handleDefault(model)}
                            data-testid={`default-radio-${model.id}`}
                            className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-500 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600"
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}

      <DisallowInUseDialog
        state={inUse}
        onClose={() => setInUse(null)}
        onConfirm={confirmMove}
        isLoading={move.isPending || update.isPending}
      />
    </section>
  );
}
